/**
 * @license
 * Copyright 2022 Superflow.dev
 * SPDX-License-Identifier: MIT
 */

import { LitElement, html, css, PropertyValueMap } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import Util from './util';
/*

Modes: multiselect, select
Flows: view,edit

*/

/**
 * SfIExternalImport element.
 * @fires renderComplete - When the list is populated
 * @fires valueChanged - When the value is changed
 * @property apiId - backend api id
 * @property label - input label
 * @property name - name of the input
 */
@customElement('sf-i-external-import')
export class SfIExternalImport extends LitElement {
  static override styles = css`
    :root {
      --color: #fcc419;
      --shadow: #f08c00;
      --glare: hsl(0 0% 100% / 0.75);
      --font-size: clamp(.75rem, 2vw, 1rem);
      --transition: 0.2s;
    }

    * {
      box-sizing: border-box;
    }

    .d-block {
      display: block;
    }

    .d-flex {
      display: flex;
    }

    .flex-col {
      flex-direction: column;
    }

    .flex-wrap {
      flex-wrap: wrap;
    }

    .justify-center {
      justify-content: center;
    }

    .justify-between {
      justify-content: space-between;
    }

    .justify-end {
      justify-content: flex-end;
    }

    .w-100-m-0 {
      width: 100%;
    }
    
    .mb-20 {
      margin-bottom: 20px;
    }
    
    .mt-20 {
      margin-top: 20px;
    }
    .mr-10 {
      margin-right: 10px;
    }
    .mr-5 {
      margin-right: 5px;
    }
    .m-0 {
      margin: 0px;
    }

    .m-20 {
      margin: 20px;
    }
    .md-20-ml-10 {
      margin-top: 20px;
      margin-bottom: 20px;
      margin-left: 10px;
      margin-right: 10px;
    }
    .mrl-5 {
      margin-left: 5px;
      margin-right: 5px;
    }
    .mrl-10 {
      margin-left: 10px;
      margin-right: 10px;
    }
    .mt-10{
      margin-top: 10px
    }
    .ml-10{
      margin-left: 10px
    }
    .mr-20{
      margin-right: 20px
    }
    .flex-grow {
      flex-grow: 1;
    }
    .align-start {
      align-items: flex-start;
    }

    .align-end {
      align-items: flex-end;
    }

    .align-center {
      align-items: center;
    }
    .align-stretch {
      align-items: stretch;
    }
    .align-self-start {
      align-self: flex-start;
    }
    body {
      display: grid;
      place-items: center;
      min-height: 100vh;
      font-family: 'Google Sans', sans-serif, system-ui;
    }
    .hide {
      display: none;
    }
    .invisible {
      visibility: hidden;
    }
    .scroll-x {
      overflow-x: scroll
    }

    .loader-element {
      position: fixed;
      right: 30px;
      top: 30px;
      margin-left: 5px;
      z-index: 102
    }

    .lds-text {
      position: absolute;
      left: 0px;
      top: 0px;
      margin-top: 2px;
      margin-left: 2px;
      width: 50px;
      height: 50px;
      color: gray;
    }

    .lds-text-c {
      display: flex;
      width: 100%;
      height: 100%;
      justify-content: center;
      align-items: center;
      margin-top: 40px;
      font-size: 90%;
    }

    .lds-dual-ring {
      display: inline-block;
      width: 50px;
      height: 50px;
    }
    .lds-dual-ring:after {
      content: " ";
      display: block;
      width: 50px;
      height: 50px;
      margin: 0px;
      border-radius: 50%;
      border: 2px solid #fff;
      border-color: #888 #ddd #888 #ddd;
      background-color: white;
      animation: lds-dual-ring 0.8s linear infinite;
    }

    .lds-dual-ring-lg {
      display: inline-block;
      width: 30px;
      height: 30px;
    }
    .lds-dual-ring-lg:after {
      content: " ";
      display: block;
      width: 30px;
      height: 30px;
      margin: 0px;
      border-radius: 50%;
      border: 3px solid #fff;
      border-color: #888 #ddd #888 #ddd;
      animation: lds-dual-ring 0.8s linear infinite;
    }

    .div-row-error {
      display: flex;
      justify-content: center;
      position: fixed;
      position: fixed;
      top: 0px;
      right: 0px;
      margin-top: 20px;
      margin-right: 20px;
      display: none;
      align-items:center;
      background-color: white;
      border: dashed 1px red;
      padding: 20px;
      z-index: 102;
    }

    .div-row-error-message {
      color: red;
      padding: 5px;
      background-color: white;
      text-align: center;
    }

    .div-row-success {
      display: flex;
      flex-direction: column;
      justify-content: center;
      position: fixed;
      top: 0px;
      right: 0px;
      margin-top: 20px;
      margin-right: 20px;
      display: none;
      align-items:stretch;
      background-color: white;
      border: dashed 1px green;
      padding: 20px;
      z-index: 102;
    }

    .div-row-success-message {
      color: green;
      padding: 5px;
      background-color: white;
      text-align: center;
    }

    .div-row-success-message-btn-container {
      background-color: white;
      display: flex;
      flex-direction: row;
      justify-content: end;
      align-items: center;
    }

    @keyframes lds-dual-ring {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }  
  `;

  @property({ type: String })
  projectid: string = '';

  @property({ type: String })
  apiId: string = '';

  @property({ type: String })
  userprofileid: string = '';

  @property({ type: String })
  username: string = '';

  @query('.div-row-error')
  _SfRowError: any;

  @query('.div-row-error-message')
  _SfRowErrorMessage: any;

  @query('.div-row-success')
  _SfRowSuccess: any;

  @query('.div-row-success-message')
  _SfRowSuccessMessage: any;

  @query('.div-row-success-message-btn-container')
  _SfRowSuccessMessageBtnContainer: any;

  @query('.loader-element')
  _SfLoader: any;

  @query('#external-reporting-file-uploader')
  _SfExternalReportingFileUploader: any;

  @query('#external-reporting-selector-import')
  _SfExternalReportingSelectorImport: any;

  @query('#external-reporting-selector-remove')
  _SfExternalReportingSelectorRemove: any;

  @query('#external-reporting-submit-btn')
  _SfExternalReportingSubmitBtn: any;

  prepareXhrPresigned = async (data: any, url: string, loaderElement: any, loaderText: string = '') => {


    if (loaderElement != null) {
      loaderElement.innerHTML = '<div class="lds-dual-ring"></div>';
      loaderElement.innerHTML += ('<div class="lds-text"><div class="lds-text-c">' + loaderText + '</div></div>');
    }
    return await Util.callApiPresigned(url, data);

  }

  prepareXhrPresignedGet = async (url: string, loaderElement: any, loaderText: string = '') => {


    if (loaderElement != null) {
      loaderElement.innerHTML = '<div class="lds-dual-ring"></div>';
      loaderElement.innerHTML += ('<div class="lds-text"><div class="lds-text-c">' + loaderText + '</div></div>');
    }
    return await Util.callApiPresignedGet(url);

  }

  prepareXhrPresignedDelete = async (url: string, loaderElement: any, loaderText: string = '') => {


    if (loaderElement != null) {
      loaderElement.innerHTML = '<div class="lds-dual-ring"></div>';
      loaderElement.innerHTML += ('<div class="lds-text"><div class="lds-text-c">' + loaderText + '</div></div>');
    }
    return await Util.callApiPresignedDelete(url);

  }


  prepareXhr = async (data: any, url: string, loaderElement: any, authorization: any, loaderText: string = '') => {


    if (loaderElement != null) {
      loaderElement.innerHTML = '<div class="lds-dual-ring"></div>';
      loaderElement.innerHTML += ('<div class="lds-text"><div class="lds-text-c">' + loaderText + '</div></div>');
    }
    return await Util.callApi(url, data, authorization);

  }

  clearMessages = () => {
    this._SfRowError.style.display = 'none';
    this._SfRowErrorMessage.innerHTML = '';
    this._SfRowSuccess.style.display = 'none';
    this._SfRowSuccessMessage.innerHTML = '';
    this._SfRowSuccessMessageBtnContainer.innerHTML = '';
  }

  setError = (msg: string) => {
    this._SfRowError.style.display = 'flex';
    this._SfRowErrorMessage.innerHTML = msg;
    this._SfRowSuccess.style.display = 'none';
    this._SfRowSuccessMessage.innerHTML = '';
    this._SfRowSuccessMessageBtnContainer.innerHTML = '';
  }

  setSuccess = (msg: string) => {
    this._SfRowError.style.display = 'none';
    this._SfRowErrorMessage.innerHTML = '';
    this._SfRowSuccess.style.display = 'flex';
    this._SfRowSuccessMessage.innerHTML = msg;
    this._SfRowSuccessMessageBtnContainer.innerHTML = '';
  }

  setSuccessBtn = (msg: string, btnCaptions: string[], btnCallbacks: Function[]) => {
    this._SfRowError.style.display = 'none';
    this._SfRowErrorMessage.innerHTML = '';
    this._SfRowSuccess.style.display = 'flex';
    this._SfRowSuccessMessage.innerHTML = msg;
    let btnHtml = ''
    for (let [i, caption] of btnCaptions.entries()) {
      btnHtml += '<button part="successmsgbtn" id="successbtn-' + i + '" class="d-flex align-center mt-10' + ((i > 0) ? ' ml-10' : '') + '">' + caption + '</button>'
    }
    this._SfRowSuccessMessageBtnContainer.innerHTML = btnHtml
    for (let [i, callback] of btnCallbacks.entries()) {
      let btn = this._SfRowSuccessMessageBtnContainer.querySelector('#successbtn-' + i) as HTMLButtonElement
      btn.addEventListener('click', () => { callback() })
    }
  }

  initListeners = () => {

    this._SfExternalReportingFileUploader.addEventListener('change', async () => {
      this.processInputFile();
    });

    this.setSubmitTitle();

    this._SfExternalReportingSelectorImport.addEventListener('change', () => {
      this.setSubmitTitle();
      this.processInputFile();
    })
    this._SfExternalReportingSelectorRemove.addEventListener('change', () => {
      this.setSubmitTitle();
      this.processInputFile();
    })

    this._SfExternalReportingSubmitBtn.addEventListener('click', () => {
      this.processInputFile();
    });
  }

  setSubmitTitle = () => {
    if (this._SfExternalReportingSelectorImport.checked) {
      this._SfExternalReportingSubmitBtn.innerHTML = "Import";
    } else {
      this._SfExternalReportingSubmitBtn.innerHTML = "Remove";
    }
  }

  processInputFile = async () => {
    if (this._SfExternalReportingFileUploader.files != null) {
      let file = this._SfExternalReportingFileUploader.files[0]
      if (file != null) {
        let jsonObj = await Util.parseMISExcel(file);
        console.log('parsed JSON', jsonObj, file.name)
        let fileName = file.name;
        let monthYearObj: any;
        try {
          monthYearObj = this.parseFileName(fileName);
          console.log('month year obj', monthYearObj)
        } catch (e) {
          this.setError("Filename format is incorrect. It should be in the format of 'report-Month-Year.xlsx', for example, 'report-March-2026.xlsx'.");
          setTimeout(() => {
            this.clearMessages();
          }, 3000);
          return;
        }
        let externalMapping = await this.fetchExternalMapping();
        console.log('external mapping', externalMapping)

        /* let countValidComplainces = 0;
        let countValidLocations = 0;
        for (let location of Object.keys(jsonObj.compliancesArr)) {
          if (externalMapping.locations.Locations[location] != null) {
            countValidLocations++;
            for (let sourceCompliance of jsonObj.compliancesArr[location]) {
              let complianceKey = sourceCompliance.act + ';' + sourceCompliance.documentName + ';' + sourceCompliance.forms
              if (externalMapping.compliances[complianceKey] != null) {
                countValidComplainces++;
              }
            }
          }
        } */

        let submitTitle = this._SfExternalReportingSubmitBtn.innerHTML;
        this.setSuccessBtn('File parsed successfully. Reporting to be updated for - ' + monthYearObj.monthName + ' ' + monthYearObj.year, ['Cancel', submitTitle], [() => {
          this.clearMessages();
        }, () => {
          console.log('Processing data...', jsonObj, monthYearObj);
          this.processCompliancesData(jsonObj, monthYearObj, externalMapping, submitTitle == "Import");
          this.clearMessages();
        }]);
      }
    } else {
      console.log('file empty')
    }
  }

  processCompliancesData = async (jsonObj: any, monthYearObj: any, externalMapping: any, flagImport: boolean) => {
    let processedData: any = {}
    for (let location of Object.keys(jsonObj.compliancesArr)) {
      if (externalMapping.locations.Locations[location] != null) {
        processedData[externalMapping.locations.Locations[location]] = {};
        for (let sourceCompliance of jsonObj.compliancesArr[location]) {
          let complianceKey = sourceCompliance.act + ';' + sourceCompliance.documentName + ';' + sourceCompliance.forms
          if (externalMapping.compliances[complianceKey] != null) {
            processedData[externalMapping.locations.Locations[location]][externalMapping.compliances[complianceKey]] = sourceCompliance.status;
          }
        }
      }
    }
    console.log('processed data', processedData, 'for', monthYearObj.month, monthYearObj.year, 'In Financial Year', monthYearObj.fincalYear);
    await this.uploadExternalReporting(processedData, monthYearObj.month, monthYearObj.fincalYear, flagImport);
  }

  months: Record<string, number> = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12,
  };

  parseFileName = (fileName: string) => {

    const match = fileName.match(/-([A-Za-z]+)-(\d{4})\.(xls|xlsx)$/i);

    if (!match) {
      throw new Error("Invalid filename format");
    }

    const month = this.months[match[1]];
    const year = Number(match[2]);
    const fincalYear = month > 3 ? Number(match[2]) : (Number(match[2]) - 1);
    console.log({ month, year, fincalYear }); // { month: 3, year: 2026, fincalYear: '2026-2027' }
    return { month, year, fincalYear, monthName:match[1] };
  }

  fetchExternalMapping = async () => {

    let url = "https://" + this.apiId + "/getallexternalmapping";
    const authorization = btoa(Util.readCookie('email') + ":" + Util.readCookie('accessToken'));
    const xhr: any = (await this.prepareXhr({ "projectid": this.projectid }, url, this._SfLoader, authorization)) as any;
    this._SfLoader.innerHTML = '';
    if (xhr.status == 200) {

      const jsonRespose = JSON.parse(xhr.responseText);
      //console.log(jsonRespose);
      return jsonRespose.data;

    } else {
      if (xhr.status == 401) {
        let changeEvent = new CustomEvent('tokenExpired', { bubbles: true });
        this.dispatchEvent(changeEvent);
      }
      const jsonRespose = JSON.parse(xhr.responseText);
      this.setError(jsonRespose.error);

    }

  }

  uploadExternalReporting = async (reportingData: any, mm: string, year: string, flagImport: boolean) => {

    let url = "https://" + this.apiId + "/uploadexternalreporting";
    const authorization = btoa(Util.readCookie('email') + ":" + Util.readCookie('accessToken'));
    const xhr: any = (await this.prepareXhr({ "projectid": this.projectid, reportedevents: reportingData, mm: ("0" + mm).slice(-2), year: year + "", userid: this.userprofileid, username: this.username, import: flagImport }, url, this._SfLoader, authorization)) as any;
    this._SfLoader.innerHTML = '';
    if (xhr.status == 200) {

      const jsonRespose = JSON.parse(xhr.responseText);
      console.log('uploadexternalreporting response', jsonRespose);
      // return jsonRespose.data;
      this.setSuccess(`The ${flagImport ? 'import' : 'remove'} action from Talentpro will happen in background and you will receive an email upon it's completion.`)
      setTimeout(() => {
        this.clearMessages()
      }, 3000)
    } else {
      if (xhr.status == 401) {
        let changeEvent = new CustomEvent('tokenExpired', { bubbles: true });
        this.dispatchEvent(changeEvent);
      }
      const jsonRespose = JSON.parse(xhr.responseText);
      this.setError(jsonRespose.error);

    }

  }

  loadMode = async () => {
    console.log("loadMode");
    this.initListeners()
  }

  protected override firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {

    this.loadMode();

  }

  override connectedCallback() {
    super.connectedCallback()
  }
  override render() {
    return html`
      <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@48,400,0,0" />
      <div part="input-outer-container" class="w-100-m-0 d-flex flex-col align-stretch">
        <div class="d-flex justify-center">
            <div class="loader-element"></div>
        </div>
        <div id="external-reporting-uploader-container" part="external-reporting-uploader-container" class="d-flex flex-col justify-start align-start mb-20">
          <span for="external-reporting-file-uploader" part="external-reporting-file-uploader-label">Upload External reporting file (.xlsx and .xls only)</span>
          <input id="external-reporting-file-uploader" part="external-reporting-file-uploader" type="file" accept=".xlsx,.xls"/>
        </div>
        <div id="external-reporting-selector-container" part="external-reporting-selector-container" class="d-flex justify-start align-center mb-20">
          <div class="d-flex justify-start align-center mb-10">
            <input id="external-reporting-selector-import" part="external-reporting-selector-import" type="radio" name="external-reporting-selector" checked/>
            <label for="external-reporting-selector-import" part="external-reporting-selector-import-label">Import</label>
          </div>
          <div class="d-flex justify-start align-center ml-10 mb-10">
            <input id="external-reporting-selector-remove" part="external-reporting-selector-remove" type="radio" name="external-reporting-selector"/>
            <label for="external-reporting-selector-remove" part="external-reporting-selector-remove-label">Remove existing reporting</label>
          </div>
        </div>
        <div id="external-reporting-submit-container" part="external-reporting-submit-container" class="d-flex justify-start align-center mb-20">
            <button id="external-reporting-submit-btn" part="button" class="mb-20">Submit</button>
        </div>
        <div class="d-flex justify-between">
            <div class="lb"></div>
            <div>
              <div class="div-row-error div-row-submit gone">
                <div part="errormsg" class="div-row-error-message"></div>
              </div>
              <div class="div-row-success div-row-submit gone">
                <div part="successmsg" class="div-row-success-message"></div>
                <div part="successmsgbtncontainer" class="div-row-success-message-btn-container"></div>
              </div>
            </div>
            <div class="rb"></div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sf-i-external-import': SfIExternalImport;
  }
}

